import type { ComponentProps } from "react";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { ariaLabelledbyProps } from "@design-system/formControl/ariaLabelledbyProps";
import { Input } from "@design-system/Input/Input";
import type { InputSize } from "@design-system/Input/Input.types";
import type { FieldPath } from "./fieldPath.types";
import { pickChromeProps } from "./kitChromeProps";
import type { KitFieldProps } from "./kitFieldProps.types";

type TextFieldValue = string | null | undefined;

/** Paths of `Values` that hold text and accept any string. */
export type TextFieldPath<Values> = FieldPath<Values, TextFieldValue, string>;

export type TextFieldProps<Name extends string> = KitFieldProps<
    Name,
    HTMLInputElement,
    string
> & {
    readOnly?: boolean;
    size?: InputSize;
    type?: ComponentProps<"input">["type"];
    placeholder?: string;
    maxLength?: number;
    autoComplete?: string;
    autoFocus?: boolean;
};

/**
 * Single-line text field bound to a typed Formik path. Renders the same
 * `Input` as `FormikField`'s default control; `null` or `undefined` values
 * show as empty text.
 */
export const TextField = <
    Values,
    Name extends TextFieldPath<Values> = TextFieldPath<Values>,
>(
    props: TextFieldProps<Name>,
) => {
    const {
        name,
        ref,
        disabled,
        readOnly,
        required,
        onValueChange,
        size,
        type,
        placeholder,
        maxLength,
        autoComplete,
        autoFocus,
    } = props;

    return (
        <FormikField<TextFieldValue> name={name} {...pickChromeProps(props)}>
            {({ field, helpers, invalid }) => (
                <Input
                    ref={ref}
                    name={name}
                    value={field.value ?? ""}
                    onValueChange={(value) => {
                        void helpers.setValue(value);
                        onValueChange?.(value);
                    }}
                    onBlur={() => void helpers.setTouched(true)}
                    invalid={invalid}
                    aria-required={required || undefined}
                    {...ariaLabelledbyProps(props["aria-labelledby"])}
                    disabled={disabled}
                    readOnly={readOnly}
                    size={size}
                    type={type}
                    placeholder={placeholder}
                    maxLength={maxLength}
                    autoComplete={autoComplete}
                    autoFocus={autoFocus}
                />
            )}
        </FormikField>
    );
};
